<template>
  <tf-table-parent-row
    :key="contact.user_id"
  >
    <tf-table-td>
      <span>
        {{ contact.full_name }}

        <template v-if="contact.email === currentUserEmail">
          (you)
          <tf-button
            v-if="contact.can_delete"
            v-test:button="'delete member'"
            type="decline"
            icon="trash-alt"
            size="icon-no-fill"
            no-border
            @click="showConfirmationCloseModal = true"
          />
          <bp-tooltip
            v-else-if="brokerTeam.length"
            placement="left"
          >
            <tf-hover-icon
              slot="reference"
              icon="regular/question-circle"
              icon-hover="question-circle"
            />

            <template v-if="brokerTeam.length === 1">
              To remove yourself,
              <tf-button
                type="primary"
                tag="a"
                size="text-small"
                @click="$emit('clickAddTeamMember')"
              >
                add another team member.
              </tf-button> Client records require at least 1 team member.
            </template>
            <template v-else>
              You are the primary contact for an active event.
              <tf-cta-email-support>
                Contact support
              </tf-cta-email-support>
              to remove yourself from this client.
            </template>
          </bp-tooltip>
        </template>
      </span>
    </tf-table-td>
    <!-- broker confirmations close modal - this will be replaced with a Shared version of this component soon -->
    <modal-notify
      :append-to-body="true"
      :visible.sync="showConfirmationCloseModal"
      custom-image="iconUsersSlash"
      type="warn"
      @close="showConfirmationCloseModal = false"
    >
      <img
        slot="customImage"
        :src="iconUsersSlash"
      >
      <template #title>
        Remove yourself from {{ clientName }}?
      </template>
      <p>You will no longer receive email updates for this client.</p>
      <tf-button
        slot="cta"
        v-test:button="'cancel remove member'"
        class="cancel-close"
        size="large"
        type="secondary"
        no-branding
        @click="showConfirmationCloseModal = false"
      >
        Cancel
      </tf-button>
      <tf-button
        slot="cta"
        v-test:button="'confirm remove member'"
        size="large"
        type="decline"
        no-branding
        @click="deleteBrokerTeamMember(contact.user_id)"
      >
        Confirm
      </tf-button>
    </modal-notify>
  </tf-table-parent-row>
</template>

<script>
  import {
    mapState,
    mapMutations,
  } from 'vuex';
  import EmployerService from '@/services/employer';
  import iconUsersSlash from '@/assets/users-slash.svg';
  import TfHoverIcon from '@/components/TfHoverIcon';
  /**
   * The "BrokerTeamTableRow" component relies on store and prop data to display an individual Broker Team Member's name and a delete icon that will
   * remove said Broker Team Member from the Client Record.
   *
   * @vuedoc
   * @exports BrokerTeamTableRow
   * @category Components
   */
  export default {
    name: 'BrokerTeamTableRow',
    components: {
      TfHoverIcon,
      ModalNotify: () => import('@/components/Modals/Notify'),
    },
    props: {
      /**
       * The client used to interact with the API.
       */
      clientId: {
        type: [String, Number],
        required: true,
      },
      /**
       * A single contact to be populated in the broker team table row.
       */
      contact: {
        type: Object,
        required: true,
      },
    },
    data: () => ({
      showConfirmationCloseModal: false,
      iconUsersSlash,
    }),
    computed: {
      ...mapState({
        clientName: (state) => state.employer.employerInfo.name,
        currentUserEmail: (state) => state.account.userInfo.email,
      }),
      ...mapState('employerBrokerTeam', ['brokerTeam']),
    },
    methods: {
      ...mapMutations('employerBrokerTeam', {
        setActionError: 'SET_ACTION_ERROR',
        setBrokerTeam: 'SET_BROKER_TEAM',
      }),
      /**
       * Delete a Broker Team Member from a Client Record.
       *
       * @param {number} userId
       */
      deleteBrokerTeamMember(userId) {
        EmployerService
          .deleteBrokerTeamMembers(
            this.clientId,
            userId,
          )
          .then((data) => {
            this.setBrokerTeam(data.broker_team_members);
            this.$message({
              duration: 3000,
              message: 'You have been removed from this client.',
              showClose: true,
              type: 'success',
              // RLH 1/28/22 customClass attached for testing purposes since adding a data test attribute here is more challenging
              customClass: 'data-test-message-success-remove-broker',
            });
          })
          .catch(({ message }) => {
            this.isSaving = false;

            /* RLH 10/25/21 - If the error code BTM-300 is returned by the DELETE request, that indicates that the delete request would have
            resulted in all users being deleted from the Broker Team, and therefore we need to open the Add Broker
            Team Members modal and prompt the user to add another team member.

            Parsing a string for an error code isn't optimal, but eventually when the BE actually has time, we hope to update this
            approach to something less brittle. */
            if (message.toLowerCase().includes('btm-300')) {
              this.setActionError('delete all users');
              this.$emit('clickAddTeamMember');
            } else {
              /* Otherwise, if any other error code was returned, display this generic error message */
              this.$message({
                duration: 3000,
                message: 'There was an error removing you from the broker team. Try again.',
                showClose: true,
                type: 'error',
              });
            }
          })
          .finally(() => {
            this.showConfirmationCloseModal = false;
          });
      },
    },
  };
</script>

<style scoped lang="scss">
  span {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  ::v-deep .tf-tooltip {
    max-width: 250px;

    .tf-button {
      display: inline;
      font-weight: normal;
    }
  }

  /* RLH 9-10-21 - The Styling below is temporarily here to support the Close Confirmation Modal
  / but will be removed entirely once the Shared Repo component is created
  */

  ::v-deep .cancel-close {
    /* stylelint-disable declaration-no-important */
    border-color: $tf-dark-gray !important;
    color: $tf-dark-gray !important;
    /* stylelint-enable declaration-no-important */
  }
</style>
